import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DocumentNeedToggle from '../components/DocumentNeedToggle';
import { newJobDocument } from '../lib/jobDocuments';
import { supabase } from '../lib/supabase';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import TopBar from '../components/TopBar';
import BottomNav from '../components/BottomNav';
import { COMMON_BENEFITS } from '../lib/constants';
import { BOHOL_MUNICIPALITIES } from '../data/boholLocations';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { usePricing } from '../hooks/usePricing';
import { Briefcase, Clock, Sparkles, PenLine, Plus, X, FileText, Shield, Save, Loader2 } from 'lucide-react';
import ImageUpload from '../components/ImageUpload';
import RichTextEditor from '../components/RichTextEditor';
import AIJobWizard from '../components/AIJobWizard';
import AIGigWizard from '../components/AIGigWizard';
import BannerCreateModal from '../components/BannerCreateModal';
import ShareJobDialog from '../components/ShareJobDialog';

/**
 * Unified Post Job/Gig Form
 * Employers can post both full-time jobs and one-time gigs
 */
const PostJob = ({ profile, setProfile }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [credits, setCredits] = useState(0);
  const { pricing, loading: pricingLoading } = usePricing();

  // Verification guard
  const [verificationPending, setVerificationPending] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState(!profile?.verified);

  // Check if verification is already pending
  useEffect(() => {
    if (profile?.verified) return;
    const checkStatus = async () => {
      try {
        const { data: session } = await supabase.auth.getSession();
        if (!session?.session?.access_token) return;
        const res = await api.get('/api/verification/status', {
          headers: { Authorization: `Bearer ${session.session.access_token}` }
        });
        setVerificationPending(res.data.status === 'pending');
      } catch {}
      setVerificationLoading(false);
    };
    checkStatus();
  }, [profile?.verified]);

  const [searchParams] = useSearchParams();

  // AI Wizard state
  const [postingMode, setPostingMode] = useState(null); // null = choosing, 'manual' = manual form, 'ai-job' = AI job wizard, 'ai-gig' = AI gig wizard

  const [formData, setFormData] = useState({
    post_type: 'job', // 'job' or 'gig'
    title: '',
    description: '',
    salary_min: '',
    salary_max: '',
    salary_period: 'monthly',
    location: '',  // Used for gigs (single location)
    locations: [],  // Used for jobs (multiple locations)
    street_address: '',
    type: 'permanent',
    work_schedule: 'full_time',
    category_id: '',
    requirements: '',
    benefits: [],
    is_featured: false,
    image_url: null,
    urgent_hiring: false,
    no_experience_needed: false,
    with_housing: false,
    custom_question: '',
    required_documents: [],
    positions_available: 1,
    image_position: 50
  });

  // DB-backed draft state
  const [draftId, setDraftId] = useState(null);
  const [draftPostType, setDraftPostType] = useState(null); // 'job' or 'gig'
  const [autoSaving, setAutoSaving] = useState(false);
  const [draftLoading, setDraftLoading] = useState(false);
  const [loadedDraftData, setLoadedDraftData] = useState(null); // Raw draft data for AI wizard resume

  // Homepage banner enticement (job-only; not sent to the job create/update payload)
  const [wantBanner, setWantBanner] = useState(false);
  const [bannerModalJobId, setBannerModalJobId] = useState(null);
  // Right after a job goes live: "Get more applicants — share this job" before the dashboard.
  const [shareJob, setShareJob] = useState(null);
  const [pendingShareJob, setPendingShareJob] = useState(null); // shown after the banner step
  const isLiveJob = (j) => j && j.id && j.status === 'open' && j.post_type !== 'gig';
  // Once a post is published the form/wizard stays mounted behind the share screen; this stops
  // the 30s draft autosave from then writing a duplicate draft (or a rejected open->draft PUT).
  const publishedRef = useRef(false);

  // Load draft from URL ?draft=ID
  useEffect(() => {
    const id = searchParams.get('draft');
    if (id) {
      setDraftLoading(true);
      loadDraft(id).finally(() => setDraftLoading(false));
    }
  }, []); // Run once on mount

  const loadDraft = async (id) => {
    try {
      // Check if draft was created via AI wizard
      let savedMode = null;
      try { savedMode = localStorage.getItem(`draft-mode-${id}`); } catch {}

      // Try jobs table first
      const { data: jobData } = await supabase
        .from('jobs')
        .select('*')
        .eq('id', id)
        .single();

      if (jobData && jobData.status === 'draft') {
        setFormData({
          post_type: 'job',
          title: jobData.title || '',
          description: jobData.description || '',
          salary_min: jobData.salary_min || '',
          salary_max: jobData.salary_max || '',
          salary_period: jobData.salary_period || 'monthly',
          location: '',
          locations: jobData.locations || [],
          street_address: jobData.street_address || '',
          type: jobData.type || 'permanent',
          work_schedule: jobData.work_schedule || 'full_time',
          category_id: jobData.category_id || '',
          requirements: (jobData.requirements || []).join('\n'),
          benefits: jobData.benefits || [],
          is_featured: false,
          image_url: jobData.image_url || null,
          urgent_hiring: jobData.urgent_hiring || false,
          no_experience_needed: jobData.no_experience_needed || false,
          with_housing: jobData.with_housing || false,
          custom_question: jobData.custom_question || '',
          required_documents: jobData.required_documents || [],
          positions_available: jobData.positions_available || 1,
          image_position: jobData.image_position || 50
        });
        setDraftId(id);
        setDraftPostType('job');
        if (savedMode === 'ai-job') {
          // Merge wizard state from localStorage (step, skills, etc.)
          let wizardState = null;
          try { wizardState = JSON.parse(localStorage.getItem(`draft-wizard-${id}`)); } catch {}
          setLoadedDraftData(wizardState ? { ...jobData, _wizardState: wizardState } : jobData);
          setPostingMode('ai-job');
        } else {
          setPostingMode('manual');
        }
        return;
      }

      // Try gigs table
      const { data: gigData } = await supabase
        .from('gigs')
        .select('*')
        .eq('id', id)
        .single();

      if (gigData && gigData.status === 'draft') {
        setFormData({
          post_type: 'gig',
          title: gigData.title || '',
          description: gigData.description || '',
          salary_min: gigData.budget_min || '',
          salary_max: gigData.budget_max || '',
          salary_period: 'monthly',
          location: gigData.location || '',
          locations: [],
          street_address: '',
          type: 'permanent',
          work_schedule: 'full_time',
          category_id: gigData.category_id || '',
          requirements: '',
          benefits: gigData.benefits || [],
          is_featured: false,
          image_url: gigData.image_url || null,
          urgent_hiring: false,
          no_experience_needed: false,
          with_housing: false,
          custom_question: '',
          required_documents: [],
          positions_available: 1,
          image_position: gigData.image_position || 50
        });
        setDraftId(id);
        setDraftPostType('gig');
        if (savedMode === 'ai-gig') {
          // Merge wizard state from localStorage (step, scope, etc.)
          let wizardState = null;
          try { wizardState = JSON.parse(localStorage.getItem(`draft-wizard-${id}`)); } catch {}
          setLoadedDraftData(wizardState ? { ...gigData, _wizardState: wizardState } : gigData);
          setPostingMode('ai-gig');
        } else {
          setPostingMode('manual');
        }
        return;
      }
    } catch (err) {
      console.error('Failed to load draft:', err);
    }
  };

  // Save draft to DB
  const saveDraft = useCallback(async (silent = false) => {
    if (publishedRef.current || !formData.title?.trim()) return;
    setAutoSaving(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      if (!token) { setAutoSaving(false); return; }

      const isGigDraft = (draftPostType || formData.post_type) === 'gig';
      const endpoint = isGigDraft ? '/api/gigs' : '/api/jobs';
      const headers = { Authorization: `Bearer ${token}` };

      // Build draft data
      const requirements = typeof formData.requirements === 'string'
        ? formData.requirements.split('\n').filter(r => r.trim()).map(r => r.trim())
        : formData.requirements || [];

      const draftData = isGigDraft ? {
        title: formData.title,
        description: formData.description || '',
        category_id: formData.category_id || null,
        location: formData.location || '',
        budget_min: parseInt(formData.salary_min) || 0,
        budget_max: parseInt(formData.salary_max) || 0,
        benefits: formData.benefits || [],
        image_url: formData.image_url || null,
        image_position: formData.image_position,
        status: 'draft'
      } : {
        title: formData.title,
        description: formData.description || '',
        category_id: formData.category_id || null,
        locations: formData.locations || [],
        street_address: formData.street_address || '',
        type: formData.type || 'permanent',
        work_schedule: formData.work_schedule || 'full_time',
        salary_min: parseInt(formData.salary_min) || 0,
        salary_max: parseInt(formData.salary_max) || 0,
        salary_period: formData.salary_period || 'monthly',
        requirements,
        benefits: formData.benefits || [],
        image_url: formData.image_url || null,
        image_position: formData.image_position,
        urgent_hiring: formData.urgent_hiring || false,
        no_experience_needed: formData.no_experience_needed || false,
        with_housing: formData.with_housing || false,
        custom_question: formData.custom_question || null,
        required_documents: formData.required_documents || [],
        positions_available: formData.positions_available || 1,
        status: 'draft'
      };

      if (draftId) {
        await api.put(`${endpoint}/${draftId}`, draftData, { headers });
      } else {
        const res = await api.post(endpoint, draftData, { headers });
        const newId = res.data.job?.id || res.data.gig?.id;
        if (newId) {
          setDraftId(newId);
          setDraftPostType(isGigDraft ? 'gig' : 'job');
          window.history.replaceState(null, '', `/post-job?draft=${newId}`);
        }
      }
      if (!silent) toast.success(t('postJob.draftSaved', 'Draft saved'));
    } catch (err) {
      console.error('Draft save failed:', err);
      if (!silent) toast.error(err.response?.data?.detail || 'Failed to save draft');
    }
    setAutoSaving(false);
  }, [formData, draftId, draftPostType, t]);

  // Auto-save every 30s for manual form
  useEffect(() => {
    if (postingMode !== 'manual' || !formData.title?.trim()) return;
    const timer = setTimeout(() => saveDraft(true), 30000);
    return () => clearTimeout(timer);
  }, [formData, postingMode, saveDraft]);

  // Save draft from AI wizard
  const handleWizardSaveDraft = useCallback(async (jobData, type = 'job') => {
    if (publishedRef.current) return;
    if (!jobData.title?.trim()) return;
    setAutoSaving(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      if (!token) { setAutoSaving(false); return; }

      const isGig = type === 'gig';
      const endpoint = isGig ? '/api/gigs' : '/api/jobs';
      const headers = { Authorization: `Bearer ${token}` };
      // Extract wizard state before sending to API
      const { _wizardState, ...apiData } = jobData;
      const draftData = { ...apiData, status: 'draft' };

      if (draftId && draftPostType === type) {
        await api.put(`${endpoint}/${draftId}`, draftData, { headers });
        // Ensure mode is stored for existing drafts too
        try { localStorage.setItem(`draft-mode-${draftId}`, isGig ? 'ai-gig' : 'ai-job'); } catch {}
        // Save wizard state (step, skills, etc.) to localStorage
        if (_wizardState) {
          try { localStorage.setItem(`draft-wizard-${draftId}`, JSON.stringify(_wizardState)); } catch {}
        }
      } else {
        const res = await api.post(endpoint, draftData, { headers });
        const newId = res.data.job?.id || res.data.gig?.id;
        if (newId) {
          setDraftId(newId);
          setDraftPostType(type);
          window.history.replaceState(null, '', `/post-job?draft=${newId}`);
          // Store posting mode so draft reopens in correct wizard
          try { localStorage.setItem(`draft-mode-${newId}`, isGig ? 'ai-gig' : 'ai-job'); } catch {}
          // Save wizard state (step, skills, etc.) to localStorage
          if (_wizardState) {
            try { localStorage.setItem(`draft-wizard-${newId}`, JSON.stringify(_wizardState)); } catch {}
          }
        }
      }
      toast.success(t('postJob.draftSaved', 'Draft saved'));
    } catch (err) {
      console.error('Wizard draft save failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to save draft');
    }
    setAutoSaving(false);
  }, [draftId, draftPostType, t]);

  // Discard in-memory draft context and reset form (draft stays in DB for later)
  const discardDraftContext = useCallback(() => {
    if (draftId) {
      try { localStorage.removeItem(`draft-mode-${draftId}`); } catch {}
      try { localStorage.removeItem(`draft-wizard-${draftId}`); } catch {}
    }
    setDraftId(null);
    setDraftPostType(null);
    setLoadedDraftData(null);
    setFormData({
      post_type: 'job',
      title: '',
      description: '',
      salary_min: '',
      salary_max: '',
      salary_period: 'monthly',
      location: '',
      locations: [],
      street_address: '',
      type: 'permanent',
      work_schedule: 'full_time',
      category_id: '',
      requirements: '',
      benefits: [],
      is_featured: false,
      image_url: null,
      urgent_hiring: false,
      no_experience_needed: false,
      with_housing: false,
      custom_question: '',
      required_documents: [],
      positions_available: 1,
      image_position: 50
    });
    window.history.replaceState(null, '', '/post-job');
    setPostingMode(null);
  }, [draftId]);

  useEffect(() => {
    fetchCategories();
    fetchCredits();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/api/categories');
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      toast.error(t('post.categoryFetchFailed', 'Failed to load categories. Please refresh the page.'));
    }
  };

  const fetchCredits = async () => {
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) return;

      const response = await api.get('/api/credits/balance', {
        headers: {
          Authorization: `Bearer ${session.session.access_token}`
        }
      });
      setCredits(response.data.balance || 0);
    } catch (error) {
      console.error('Failed to fetch credits:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast.error('Please log in');
        setLoading(false);
        return;
      }

      // Validate required text fields
      if (!formData.title || formData.title.trim().length < 3) {
        toast.error('Please enter a job title (at least 3 characters)');
        setLoading(false);
        return;
      }

      if (!formData.description || formData.description.trim().length < 20) {
        toast.error('Please enter a job description (at least 20 characters)');
        setLoading(false);
        return;
      }

      if (!formData.category_id) {
        toast.error('Please select a category');
        setLoading(false);
        return;
      }

      // Validate required fields
      const isGig = formData.post_type === 'gig';
      if (!isGig && (!formData.locations || formData.locations.length === 0)) {
        toast.error('Please select at least one location');
        setLoading(false);
        return;
      }
      if (isGig && !formData.location) {
        toast.error('Please select a municipality/city');
        setLoading(false);
        return;
      }

      // Salary/budget is REQUIRED. For jobs, enforce a per-period minimum so employers
      // can't hide the real pay with a token value (e.g. ₱1). The maximum is optional.
      const isGigPost = draftId ? draftPostType === 'gig' : formData.post_type === 'gig';
      if (!formData.salary_min) {
        toast.error(isGigPost ? 'Please enter the minimum budget' : 'Please enter the minimum salary');
        setLoading(false);
        return;
      }
      const salaryMin = Math.floor(Number(formData.salary_min));
      if (isNaN(salaryMin) || salaryMin <= 0) {
        toast.error(`Please enter a valid minimum ${isGigPost ? 'budget' : 'salary'} (must be greater than 0)`);
        setLoading(false);
        return;
      }
      if (!isGigPost) {
        const MIN_SALARY_BY_PERIOD = { daily: 100, monthly: 1000 };
        const floor = MIN_SALARY_BY_PERIOD[formData.salary_period] || 1000;
        if (salaryMin < floor) {
          toast.error(`For ${formData.salary_period} pay, the minimum salary must be at least ₱${floor.toLocaleString()}. Please enter the real salary.`);
          setLoading(false);
          return;
        }
      }
      // Maximum is optional; if provided it must be a valid number that is >= the minimum.
      let salaryMax = salaryMin;
      if (formData.salary_max !== '' && formData.salary_max != null) {
        const parsedMax = Math.floor(Number(formData.salary_max));
        if (!isNaN(parsedMax) && parsedMax > 0) {
          if (parsedMax < salaryMin) {
            toast.error(`Maximum ${isGigPost ? 'budget' : 'salary'} cannot be less than the minimum`);
            setLoading(false);
            return;
          }
          salaryMax = parsedMax;
        }
      }

      // Parse requirements (with null safety)
      const requirements = (formData.requirements || '')
        .split('\n')
        .filter(r => r.trim())
        .map(r => r.trim());

      // Route to correct endpoint based on post type (isGigPost computed above)
      const endpoint = isGigPost ? '/api/gigs' : '/api/jobs';

      // Prepare data - gigs use budget_min/max, jobs use salary_min/max
      const postData = isGigPost ? {
        title: formData.title,
        description: formData.description,
        category_id: formData.category_id,
        location: formData.location,
        budget_min: salaryMin,
        budget_max: salaryMax,
        benefits: formData.benefits || [],
        image_url: formData.image_url || null,
        image_position: formData.image_position,
        is_featured: formData.is_featured || false,
        post_type: 'gig'
      } : {
        title: formData.title,
        description: formData.description,
        category_id: formData.category_id,
        locations: formData.locations,
        street_address: formData.street_address || '',
        type: formData.type || 'permanent',
        work_schedule: formData.work_schedule || 'full_time',
        salary_min: salaryMin,
        salary_max: salaryMax,
        salary_period: formData.salary_period || 'monthly',
        requirements,
        benefits: formData.benefits || [],
        image_url: formData.image_url || null,
        image_position: formData.image_position,
        urgent_hiring: formData.urgent_hiring || false,
        no_experience_needed: formData.no_experience_needed || false,
        with_housing: formData.with_housing || false,
        custom_question: formData.custom_question || null,
        required_documents: formData.required_documents || [],
        positions_available: formData.positions_available || 1,
        is_featured: formData.is_featured || false,
        post_type: 'job'
      };

      let response;
      if (draftId) {
        // Publish existing draft via PUT — strip fields blocked by backend
        const { is_featured, post_type, ...publishData } = postData;
        response = await api.put(
          `${endpoint}/${draftId}`,
          { ...publishData, status: 'open' },
          { headers: { Authorization: `Bearer ${session.session.access_token}` } }
        );
      } else {
        // Create new post via POST
        response = await api.post(
          endpoint,
          postData,
          { headers: { Authorization: `Bearer ${session.session.access_token}` } }
        );
      }

      if (!response.data || !response.data.success) {
        throw new Error(response.data?.detail || 'Failed to post');
      }

      publishedRef.current = true;
      const postType = isGigPost ? 'Gig' : 'Job';
      toast.success(`${postType} posted successfully!`);

      // If the employer opted to also feature this job on the homepage banner,
      // open the create flow prefilled with the new job instead of navigating away.
      const newJob = response.data.job;
      const newJobId = newJob?.id;
      const shareable = !isGigPost && isLiveJob(newJob) ? newJob : null;
      if (!isGigPost && wantBanner && newJobId) {
        setPendingShareJob(shareable);
        setBannerModalJobId(newJobId);
      } else if (shareable) {
        setShareJob(shareable);
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Post job error:', error);
      toast.error(error.response?.data?.detail || 'Failed to post');
    } finally {
      setLoading(false);
    }
  };

  const toggleBenefit = (benefit) => {
    setFormData(prev => ({
      ...prev,
      benefits: prev.benefits.includes(benefit)
        ? prev.benefits.filter(b => b !== benefit)
        : [...prev.benefits, benefit]
    }));
  };

  const isGig = formData.post_type === 'gig';

  // Handle AI wizard completion
  const handleAIWizardComplete = async (jobData) => {
    setLoading(true);
    let jobPosted = false;
    let postedJob = null;

    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast.error('Please log in');
        setLoading(false);
        return;
      }

      const headers = { Authorization: `Bearer ${session.session.access_token}` };
      let response;

      if (draftId && draftPostType === 'job') {
        // Publish existing job draft via PUT — strip fields blocked by backend
        const { is_featured, post_type, ...publishData } = jobData;
        response = await api.put(`/api/jobs/${draftId}`, { ...publishData, status: 'open' }, { headers });
      } else {
        response = await api.post('/api/jobs', jobData, { headers });
      }

      if (response.data && response.data.success === true) {
        jobPosted = true;
        publishedRef.current = true;
        postedJob = response.data.job || null;
      } else if (response.data && response.data.detail) {
        throw new Error(response.data.detail);
      } else {
        throw new Error(response.data?.detail || 'Failed to post job');
      }

    } catch (error) {
      console.error('Post job error:', error);
      const errorDetail = error.response?.data?.detail || error.message || 'Failed to post job';

      if (errorDetail.includes('verify') || errorDetail.includes('verified')) {
        toast.error('Please verify your account first before posting jobs.');
      } else if (errorDetail.includes('employer') || errorDetail.includes('Employer')) {
        toast.error('Only employer accounts can post jobs. Please update your account type.');
      } else if (errorDetail.includes('credit') || errorDetail.includes('Credit')) {
        toast.error(errorDetail);
      } else {
        toast.error(errorDetail);
      }
    } finally {
      setLoading(false);

      if (jobPosted) {
        toast.success('Job posted successfully!');
        if (isLiveJob(postedJob)) setShareJob(postedJob);
        else navigate('/dashboard');
      }
    }
  };

  // Render mode choice screen
  const renderModeChoice = () => (
    <Card>
      <CardHeader className="text-center">
        <CardTitle className="text-2xl font-heading">{t('post.createPost', 'Create Post')}</CardTitle>
        <CardDescription>
          {t('post.aiWizard.chooseMethod', 'What type of post would you like to create?')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* AI Job Wizard Option */}
        <button
          onClick={() => setPostingMode('ai-job')}
          className="w-full p-6 border-2 border-primary/20 rounded-2xl hover:border-primary hover:bg-primary/5 transition-colors text-left group"
        >
          <div className="flex items-start gap-4">
            <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl group-hover:scale-110 transition-transform">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold mb-1">{t('post.aiWizard.aiJobWizard', 'AI Job Wizard')}</h3>
              <p className="text-muted-foreground text-sm mb-3">
                {t('post.aiWizard.aiJobDesc', 'Hire for a permanent position (full-time or part-time). AI helps you create an attractive job listing.')}
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">{t('post.permanent', 'Permanent Position')}</span>
                <span className="text-xs bg-pink-100 text-pink-700 px-2 py-1 rounded-full">{t('post.aiWizard.aiPowered', 'AI-Powered')}</span>
              </div>
            </div>
          </div>
        </button>

        {/* AI Gig Wizard Option */}
        <button
          onClick={() => setPostingMode('ai-gig')}
          className="w-full p-6 border-2 border-orange-200 rounded-2xl hover:border-orange-400 hover:bg-orange-50 transition-colors text-left group"
        >
          <div className="flex items-start gap-4">
            <div className="p-3 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl group-hover:scale-110 transition-transform">
              <Clock className="w-8 h-8 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold mb-1">{t('gig.aiWizard.title', 'AI Gig Wizard')}</h3>
              <p className="text-muted-foreground text-sm mb-3">
                {t('gig.aiWizard.desc', 'Post a one-time task or short-term project. Workers bid on your gig.')}
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full">{t('post.oneTimeGig', 'One-time Task')}</span>
                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">{t('post.aiWizard.aiPowered', 'AI-Powered')}</span>
              </div>
            </div>
          </div>
        </button>

        {/* Manual Option */}
        <button
          onClick={() => setPostingMode('manual')}
          className="w-full p-6 border-2 border-gray-200 rounded-2xl hover:border-gray-300 hover:bg-gray-50 transition-colors text-left"
        >
          <div className="flex items-start gap-4">
            <div className="p-3 bg-gray-100 rounded-xl">
              <PenLine className="w-8 h-8 text-gray-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold mb-1">{t('post.aiWizard.writeMyself', 'Write it myself')}</h3>
              <p className="text-muted-foreground text-sm mb-3">
                {t('post.aiWizard.writeMyselfDesc', 'Full control over every detail. Traditional form for jobs or gigs.')}
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">{t('post.aiWizard.fullControl', 'Full Control')}</span>
                <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">{t('post.aiWizard.customContent', 'Custom Content')}</span>
              </div>
            </div>
          </div>
        </button>

        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="w-full mt-4"
        >
          {t('common.cancel', 'Cancel')}
        </Button>
      </CardContent>
    </Card>
  );

  // Handle AI Gig wizard completion - gig already posted by wizard
  const handleAIGigWizardComplete = (gigData) => {
    // Success toast already shown by wizard, just navigate
    navigate('/dashboard');
  };

  // Verification guard — block employers who are neither ID-verified NOR PESO-approved.
  // PESO-registered employers are approved by PESO staff (business-permit check on their
  // dashboard), not by uploading an ID — so peso_approved is an equally valid gate to post.
  if (!profile?.verified && !profile?.peso_approved) {
    const isPesoEmployer = !!(profile?.registration_source && String(profile.registration_source).startsWith('peso_'));
    return (
      <div className="min-h-screen-safe pb-nav md:pb-0">
        <TopBar profile={profile} setProfile={setProfile} />
        <div className="px-4 py-12 max-w-2xl mx-auto text-center space-y-6">
          {verificationLoading ? (
            <div className="animate-pulse space-y-4">
              <div className="h-12 w-12 bg-muted rounded-full mx-auto" />
              <div className="h-6 w-48 bg-muted rounded mx-auto" />
              <div className="h-4 w-72 bg-muted rounded mx-auto" />
            </div>
          ) : isPesoEmployer ? (
            <>
              <Shield size={48} className="mx-auto text-blue-600" />
              <h2 className="text-2xl font-heading font-semibold">
                {t('postJob.pesoApprovalPending', 'PESO Panglao Approval Pending')}
              </h2>
              <p className="text-muted-foreground">
                {t('postJob.pesoApprovalPendingDesc', 'Your registration has been received. PESO Panglao will verify your business permit directly — no ID upload needed. Once their staff approve your establishment, you can post job vacancies here.')}
              </p>
              <Button variant="outline" onClick={() => navigate('/dashboard')}>
                {t('postJob.backToDashboard', 'Back to Dashboard')}
              </Button>
            </>
          ) : verificationPending ? (
            <>
              <Clock size={48} className="mx-auto text-yellow-500" />
              <h2 className="text-2xl font-heading font-semibold">
                {t('postJob.verificationPending', 'Verification Under Review')}
              </h2>
              <p className="text-muted-foreground">
                {t('postJob.verificationPendingDesc', 'Your ID documents have been submitted and are being reviewed. This usually takes 24-48 hours. You\'ll be able to post jobs once approved.')}
              </p>
              <Button variant="outline" onClick={() => navigate('/dashboard')}>
                {t('postJob.backToDashboard', 'Back to Dashboard')}
              </Button>
            </>
          ) : (
            <>
              <Shield size={48} className="mx-auto text-primary" />
              <h2 className="text-2xl font-heading font-semibold">
                {t('postJob.verificationRequired', 'Verify Your ID to Post Jobs')}
              </h2>
              <p className="text-muted-foreground">
                {t('postJob.verificationRequiredDesc', 'To protect our community, we require ID verification before posting jobs. This helps job seekers trust that your listings are legitimate.')}
              </p>
              <Button onClick={() => navigate('/verify-id')}>
                <Shield size={16} className="mr-2" />
                {t('verification.verifyNow', 'Verify Now')}
              </Button>
            </>
          )}
        </div>
        <BottomNav profile={profile} />
      </div>
    );
  }

  // If showing AI Job wizard
  // The job is live: ask the employer to share it before going to the dashboard (rendered on both
  // the form screen and the AI-wizard screen, whichever posted it).
  const shareJobDialog = (
    <ShareJobDialog
      job={shareJob}
      open={!!shareJob}
      justPosted
      onClose={() => {
        setShareJob(null);
        navigate('/dashboard');
      }}
    />
  );

  if (postingMode === 'ai-job') {
    return (
      <div className="min-h-screen-safe pb-nav md:pb-0">
        <TopBar profile={profile} setProfile={setProfile} />
        <div className="px-4 py-6 max-w-3xl mx-auto">
          <AIJobWizard
            categories={categories}
            onComplete={handleAIWizardComplete}
            onCancel={draftId ? discardDraftContext : () => setPostingMode(null)}
            onSaveDraft={(data) => handleWizardSaveDraft(data, 'job')}
            draftId={draftPostType === 'job' ? draftId : null}
            autoSaving={autoSaving}
            initialDraftData={draftPostType === 'job' ? loadedDraftData : null}
          />
        </div>
        {shareJobDialog}
        <BottomNav profile={profile} />
      </div>
    );
  }

  // If showing AI Gig wizard
  if (postingMode === 'ai-gig') {
    return (
      <div className="min-h-screen-safe pb-nav md:pb-0">
        <TopBar profile={profile} setProfile={setProfile} />
        <div className="px-4 py-6 max-w-3xl mx-auto">
          <AIGigWizard
            categories={categories}
            onComplete={handleAIGigWizardComplete}
            onCancel={draftId ? discardDraftContext : () => setPostingMode(null)}
            onSaveDraft={(data) => handleWizardSaveDraft(data, 'gig')}
            draftId={draftPostType === 'gig' ? draftId : null}
            autoSaving={autoSaving}
            initialDraftData={draftPostType === 'gig' ? loadedDraftData : null}
          />
        </div>
        <BottomNav profile={profile} />
      </div>
    );
  }

  // Loading draft from URL
  if (draftLoading) {
    return (
      <div className="min-h-screen-safe pb-nav md:pb-0">
        <TopBar profile={profile} setProfile={setProfile} />
        <div className="px-4 py-12 max-w-2xl mx-auto text-center space-y-4">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground">{t('postJob.loadingDraft', 'Loading draft...')}</p>
        </div>
        <BottomNav profile={profile} />
      </div>
    );
  }

  // If still choosing mode
  if (postingMode === null) {
    return (
      <div className="min-h-screen-safe pb-nav md:pb-0">
        <TopBar profile={profile} setProfile={setProfile} />
        <div className="px-4 py-6 max-w-md mx-auto">
          {renderModeChoice()}
        </div>
        <BottomNav profile={profile} />
      </div>
    );
  }

  // Manual posting mode (original form)
  return (
    <div className="min-h-screen-safe pb-nav md:pb-0">
      <TopBar profile={profile} setProfile={setProfile} />

      <div className="px-4 py-6 max-w-3xl mx-auto">
        {/* Back to mode selection */}
        <Button
          variant="ghost"
          onClick={draftId ? discardDraftContext : () => setPostingMode(null)}
          className="mb-4"
        >
          <PenLine className="w-4 h-4 mr-2" />
          Back to options
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl font-heading">{t('post.postJob')}</CardTitle>
            <CardDescription>
              {t('post.jobDescription') || 'Hire workers for full-time positions or one-time projects'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="post-form">
              
              {/* POST TYPE SELECTOR */}
              <div className="space-y-2">
                <Label htmlFor="post_type">What are you posting? *</Label>
                <Select
                  value={formData.post_type}
                  onValueChange={(value) => setFormData({ ...formData, post_type: value })}
                  required
                  disabled={!!draftId}
                >
                  <SelectTrigger className="rounded-xl" data-testid="post-type-selector">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="job">
                      <div className="flex items-center gap-2">
                        <Briefcase size={16} />
                        <div>
                          <div className="font-semibold">Permanent Position</div>
                          <div className="text-xs text-muted-foreground">Long-term employment opportunity</div>
                        </div>
                      </div>
                    </SelectItem>
                    <SelectItem value="gig">
                      <div className="flex items-center gap-2">
                        <Clock size={16} />
                        <div>
                          <div className="font-semibold">One-time Gig</div>
                          <div className="text-xs text-muted-foreground">Short-term project or task</div>
                        </div>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* TITLE */}
              <div className="space-y-2">
                <Label htmlFor="title">{isGig ? 'Gig Title' : 'Job Title'} *</Label>
                <Input
                  id="title"
                  placeholder={isGig ? "e.g., Move furniture, Paint house" : "e.g., Restaurant Server, Construction Worker"}
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="rounded-xl"
                  data-testid="post-form-title"
                />
              </div>

              {/* CATEGORY & WORK SCHEDULE */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category">Category *</Label>
                  <Select
                    value={formData.category_id}
                    onValueChange={(value) => setFormData({ ...formData, category_id: value })}
                    required
                  >
                    <SelectTrigger className="rounded-xl" data-testid="post-form-category">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Work Schedule (for permanent jobs only - employment type is determined by post_type) */}
                {!isGig && (
                  <div className="space-y-2">
                    <Label htmlFor="work_schedule">Work Schedule *</Label>
                    <Select
                      value={formData.work_schedule}
                      onValueChange={(value) => setFormData({ ...formData, work_schedule: value })}
                      required
                    >
                      <SelectTrigger className="rounded-xl" data-testid="post-form-work-schedule">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="full_time">
                          <div>
                            <div className="font-semibold">Full Time</div>
                            <div className="text-xs text-muted-foreground">Regular hours (8hrs/day)</div>
                          </div>
                        </SelectItem>
                        <SelectItem value="part_time">
                          <div>
                            <div className="font-semibold">Part Time</div>
                            <div className="text-xs text-muted-foreground">Flexible/reduced hours</div>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {/* LOCATION */}
              <div className="space-y-4 p-4 border rounded-xl bg-gray-50">
                <div className="flex items-center gap-2 mb-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
                  <h3 className="font-semibold text-sm">{isGig ? 'Gig Location' : 'Job Location'}</h3>
                </div>

                {isGig ? (
                  <div className="space-y-2">
                    <Label htmlFor="location">Municipality/City *</Label>
                    <Select
                      value={formData.location}
                      onValueChange={(value) => setFormData({ ...formData, location: value })}
                      required
                    >
                      <SelectTrigger className="rounded-xl bg-white" data-testid="post-form-location">
                        <SelectValue placeholder="Select municipality or city" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {BOHOL_MUNICIPALITIES.map((municipality) => (
                          <SelectItem key={municipality} value={municipality}>{municipality}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label>Municipalities/Cities *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button type="button" className="flex w-full items-center justify-between rounded-xl border border-input bg-white px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 min-h-[40px]" data-testid="post-form-locations">
                          <span className={formData.locations.length === 0 ? 'text-muted-foreground' : ''}>
                            {formData.locations.length === 0 ? 'Select municipalities' : `${formData.locations.length} selected`}
                          </span>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-50"><path d="m6 9 6 6 6-6"/></svg>
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                        <div className="max-h-[250px] overflow-y-auto p-2" onWheel={(e) => e.stopPropagation()}>
                          {BOHOL_MUNICIPALITIES.map((municipality) => (
                            <label key={municipality} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent cursor-pointer text-sm">
                              <Checkbox
                                checked={formData.locations.includes(municipality)}
                                onCheckedChange={(checked) => {
                                  const next = checked
                                    ? [...formData.locations, municipality]
                                    : formData.locations.filter(l => l !== municipality);
                                  setFormData({ ...formData, locations: next });
                                }}
                              />
                              {municipality}
                            </label>
                          ))}
                        </div>
                      </PopoverContent>
                    </Popover>
                    {formData.locations.length > 0 && (
                      <p className="text-xs text-muted-foreground">{formData.locations.join(', ')}</p>
                    )}
                  </div>
                )}

                {/* Street Address */}
                <div className="space-y-2">
                  <Label htmlFor="street_address">Street Address or Landmark (Optional)</Label>
                  <Input
                    id="street_address"
                    placeholder="e.g. Near Bohol Quality Mall, J.A. Clarin St."
                    value={formData.street_address}
                    onChange={(e) => setFormData({ ...formData, street_address: e.target.value })}
                    className="rounded-xl bg-white"
                    data-testid="post-form-street-address"
                  />
                  <p className="text-xs text-muted-foreground">
                    Add specific street, building name, or nearby landmark
                  </p>
                </div>
              </div>

              {/* DESCRIPTION */}
              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <RichTextEditor
                  value={formData.description}
                  onChange={(value) => setFormData({ ...formData, description: value })}
                  placeholder={isGig ? "Describe the work to be done, timeline, and any special requirements..." : "Describe the role, responsibilities, and work environment..."}
                  className="rounded-xl"
                />
              </div>

              {/* SALARY/BUDGET RANGE - NOW REQUIRED */}
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="salary_min">{isGig ? 'Budget Min' : 'Salary Min'} (₱) *</Label>
                    <Input
                      id="salary_min"
                      type="number"
                      placeholder={isGig ? "5000" : "15000"}
                      value={formData.salary_min}
                      onChange={(e) => setFormData({ ...formData, salary_min: e.target.value })}
                      onWheel={(e) => e.target.blur()}
                      className="rounded-xl"
                      data-testid="post-form-salary-min"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="salary_max">{isGig ? 'Budget Max' : 'Salary Max'} (₱)</Label>
                    <Input
                      id="salary_max"
                      type="number"
                      placeholder={isGig ? "15000" : "25000"}
                      value={formData.salary_max}
                      onChange={(e) => setFormData({ ...formData, salary_max: e.target.value })}
                      onWheel={(e) => e.target.blur()}
                      className="rounded-xl"
                      data-testid="post-form-salary-max"
                    />
                  </div>
                </div>

                {/* SALARY PERIOD (for non-gig jobs only) */}
                {!isGig && (
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="salary_period">Salary Period *</Label>
                      <Select
                        value={formData.salary_period}
                        onValueChange={(value) => setFormData({ ...formData, salary_period: value })}
                        required
                      >
                        <SelectTrigger className="rounded-xl" data-testid="post-form-salary-period">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="monthly">
                            <div>
                              <div className="font-semibold">Monthly</div>
                              <div className="text-xs text-muted-foreground">Salary paid per month</div>
                            </div>
                          </SelectItem>
                          <SelectItem value="daily">
                            <div>
                              <div className="font-semibold">Daily</div>
                              <div className="text-xs text-muted-foreground">Salary paid per day</div>
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        This helps match with job seekers' salary expectations
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="positions_available">Number of Positions</Label>
                      <Input
                        id="positions_available"
                        type="number"
                        min="1"
                        max="100"
                        placeholder="1"
                        value={formData.positions_available}
                        onChange={(e) => setFormData({ ...formData, positions_available: Math.max(1, parseInt(e.target.value) || 1) })}
                        onWheel={(e) => e.target.blur()}
                        className="rounded-xl"
                        data-testid="post-form-positions"
                      />
                      <p className="text-xs text-muted-foreground">
                        How many people are you hiring for this position?
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* REQUIREMENTS */}
              <div className="space-y-2">
                <Label htmlFor="requirements">Requirements (one per line)</Label>
                <Textarea
                  id="requirements"
                  placeholder={isGig ? "Tools needed\nExperience level\nTime availability" : "High school diploma\nExperience with customer service\nGood communication skills"}
                  value={formData.requirements}
                  onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
                  rows={4}
                  className="rounded-xl"
                  data-testid="post-form-requirements"
                />
              </div>

              {/* BENEFITS (only for jobs, not gigs) */}
              {!isGig && (
                <div className="space-y-3">
                  <Label>Benefits & Perks</Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {COMMON_BENEFITS.map((benefit) => (
                      <div key={benefit} className="flex items-center space-x-2">
                        <Checkbox
                          id={benefit}
                          checked={formData.benefits.includes(benefit)}
                          onCheckedChange={() => toggleBenefit(benefit)}
                          data-testid={`benefit-${benefit}`}
                        />
                        <label
                          htmlFor={benefit}
                          className="text-sm cursor-pointer select-none"
                        >
                          {benefit}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* QUICK FILTERS & TAGS */}
              <div className="space-y-3 border-t pt-6">
                <Label className="text-base font-semibold">Quick Filters & Tags</Label>
                <p className="text-sm text-muted-foreground">
                  Help job seekers find your posting with these special tags
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Urgent Hiring */}
                  <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-secondary/50 transition-colors">
                    <Checkbox
                      id="urgent_hiring"
                      checked={formData.urgent_hiring}
                      onCheckedChange={(checked) => setFormData({ ...formData, urgent_hiring: checked })}
                      data-testid="urgent-hiring-checkbox"
                    />
                    <div className="flex-1">
                      <label
                        htmlFor="urgent_hiring"
                        className="text-sm font-medium cursor-pointer block"
                      >
                        🔴 Urgent Hiring
                      </label>
                      <p className="text-xs text-muted-foreground mt-1">
                        Need to fill this position quickly
                      </p>
                    </div>
                  </div>

                  {/* No Experience Needed */}
                  <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-secondary/50 transition-colors">
                    <Checkbox
                      id="no_experience_needed"
                      checked={formData.no_experience_needed}
                      onCheckedChange={(checked) => setFormData({ ...formData, no_experience_needed: checked })}
                      data-testid="no-experience-checkbox"
                    />
                    <div className="flex-1">
                      <label
                        htmlFor="no_experience_needed"
                        className="text-sm font-medium cursor-pointer block"
                      >
                        ⭐ No Experience Needed
                      </label>
                      <p className="text-xs text-muted-foreground mt-1">
                        Open to fresh graduates and beginners
                      </p>
                    </div>
                  </div>

                  {/* With Housing */}
                  <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-secondary/50 transition-colors">
                    <Checkbox
                      id="with_housing"
                      checked={formData.with_housing}
                      onCheckedChange={(checked) => setFormData({ ...formData, with_housing: checked })}
                      data-testid="with-housing-checkbox"
                    />
                    <div className="flex-1">
                      <label
                        htmlFor="with_housing"
                        className="text-sm font-medium cursor-pointer block"
                      >
                        🏠 With Housing
                      </label>
                      <p className="text-xs text-muted-foreground mt-1">
                        Accommodation provided
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* CUSTOM SCREENING QUESTION (Jobs only) */}
              {!isGig && (
                <div className="space-y-3 border-t pt-6">
                  <Label htmlFor="custom_question" className="text-base font-semibold">
                    Screening Question (Optional)
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Add a question that applicants must answer when applying. Their answer will be visible when reviewing applicants.
                  </p>
                  <Textarea
                    id="custom_question"
                    placeholder="e.g., Why are you interested in this position? / Do you have your own transportation?"
                    value={formData.custom_question}
                    onChange={(e) => setFormData({ ...formData, custom_question: e.target.value })}
                    className="rounded-xl bg-white min-h-[80px]"
                    maxLength={500}
                  />
                  <p className="text-xs text-muted-foreground text-right">
                    {formData.custom_question.length}/500 characters
                  </p>
                </div>
              )}

              {/* REQUIRED DOCUMENTS (Jobs only) */}
              {!isGig && (
                <div className="space-y-3 border-t pt-6">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <FileText size={18} />
                    {t('jobDocs.heading', 'Documents from applicants (optional)')}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {t('jobDocs.formHint', "List the documents you'll need. For each one choose \"To apply\" (they must upload it before sending the application) or \"If hired\" (they can apply now and bring it later).")}
                  </p>

                  {/* Application-impact warning — required docs block applying */}
                  <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3">
                    <Shield size={18} className="mt-0.5 shrink-0 text-amber-600" />
                    <div className="text-sm text-amber-900">
                      <span className="font-semibold">{t('jobDocs.warnTitle', 'Heads up:')}</span>{' '}
                      {t('jobDocs.warnBody', "Job seekers can't apply until they upload every document marked \"To apply\". Asking for clearances up front is the #1 reason jobs get zero applicants — mark NBI, medical, birth certificate, TIN and similar as \"If hired\". You'll still see what each applicant hasn't sent and can ask for it in chat.")}
                    </div>
                  </div>

                  {/* Document list */}
                  {formData.required_documents.length > 0 && (
                    <div className="space-y-2">
                      {formData.required_documents.map((doc, index) => (
                        <div key={index} className="flex flex-wrap items-center gap-2 bg-gray-50 rounded-lg p-2">
                          <FileText size={16} className="text-gray-500 shrink-0" />
                          <span className="flex-1 min-w-[7.5rem] text-sm break-words">{doc.name}</span>
                          <DocumentNeedToggle
                            doc={doc}
                            onChange={(updated) => {
                              const newDocs = [...formData.required_documents];
                              newDocs[index] = updated;
                              setFormData({ ...formData, required_documents: newDocs });
                            }}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 min-w-[44px] min-h-[44px]"
                            onClick={() => {
                              const newDocs = [...formData.required_documents];
                              newDocs.splice(index, 1);
                              setFormData({ ...formData, required_documents: newDocs });
                            }}
                          >
                            <X size={16} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add document form */}
                  {formData.required_documents.length < 10 && (
                    <div className="flex gap-2">
                      <Input
                        id="new_document_name"
                        placeholder="Document name (e.g., Resume, NBI Clearance)"
                        className="rounded-xl bg-white flex-1"
                        maxLength={50}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const docName = e.target.value.trim();
                            if (docName && !formData.required_documents.some(d => d.name.toLowerCase() === docName.toLowerCase())) {
                              setFormData({
                                ...formData,
                                required_documents: [...formData.required_documents, newJobDocument(docName)]
                              });
                              e.target.value = '';
                            }
                          }
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        className="rounded-xl"
                        onClick={() => {
                          const input = document.getElementById('new_document_name');
                          const docName = input.value.trim();
                          if (docName && !formData.required_documents.some(d => d.name.toLowerCase() === docName.toLowerCase())) {
                            setFormData({
                              ...formData,
                              required_documents: [...formData.required_documents, newJobDocument(docName)]
                            });
                            input.value = '';
                          }
                        }}
                      >
                        <Plus size={16} className="mr-1" />
                        Add
                      </Button>
                    </div>
                  )}

                  {/* Quick add suggestions */}
                  <div className="flex flex-wrap gap-2">
                    {['Resume', 'NBI Clearance', 'Police Clearance', 'Medical Certificate', 'Birth Certificate'].map((suggestion) => (
                      !formData.required_documents.some(d => d.name.toLowerCase() === suggestion.toLowerCase()) && (
                        <Button
                          key={suggestion}
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-xs h-7 px-2 bg-gray-100 hover:bg-gray-200"
                          onClick={() => {
                            if (formData.required_documents.length < 10) {
                              setFormData({
                                ...formData,
                                required_documents: [...formData.required_documents, newJobDocument(suggestion)]
                              });
                            }
                          }}
                        >
                          + {suggestion}
                        </Button>
                      )
                    ))}
                  </div>

                  {formData.required_documents.length >= 10 && (
                    <p className="text-xs text-amber-600">Maximum 10 documents allowed</p>
                  )}
                </div>
              )}

              {/* IMAGE UPLOAD */}
              <div className="space-y-3">
                <ImageUpload
                  onImageUpload={(imageUrl) => setFormData(prev => ({ ...prev, image_url: imageUrl }))}
                  currentImage={formData.image_url}
                  label={`${isGig ? 'Gig' : 'Job'} Image (Optional)`}
                  imagePosition={formData.image_position}
                  onPositionChange={(pos) => setFormData(prev => ({ ...prev, image_position: pos }))}
                />
              </div>

              {/* FEATURE OPTION */}
              <div className="border-t pt-6">
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="is_featured"
                    checked={formData.is_featured}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_featured: checked === true })}
                    disabled={credits < (pricing.featured_post || 30)}
                    data-testid="feature-checkbox"
                  />
                  <div className="flex-1">
                    <label
                      htmlFor="is_featured"
                      className="text-sm font-medium cursor-pointer"
                    >
                      ⭐ Feature this posting ({pricing.featured_post} credits)
                    </label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Featured postings appear at the top for 7 days. Your balance: {credits} credits
                    </p>
                    {credits < (pricing.featured_post || 30) && formData.is_featured && (
                      <p className="text-xs text-red-500 mt-1">Insufficient credits. Please purchase more credits.</p>
                    )}
                  </div>
                </div>
              </div>

              {/* HOMEPAGE BANNER OPTION (jobs only — banners link to a job, not a gig).
                  Bright-gold treatment (user request): saturated gradient + glow so it's
                  impossible to miss, mirroring the "Featured" gold identity. */}
              {!isGig && pricing.banner_show_job_create && (
                <div className="border-t pt-6">
                  <div className="rounded-xl border-[1.5px] border-[#E08A0B] bg-gradient-to-br from-[#FFE067] to-[#F5A623] shadow-[0_6px_22px_-6px_rgba(240,160,20,0.6)] p-4">
                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="want_banner"
                        checked={wantBanner}
                        onCheckedChange={(checked) => setWantBanner(checked === true)}
                        className="mt-0.5 border-[#a86a06] data-[state=checked]:bg-[#B45309] data-[state=checked]:border-[#B45309]"
                        data-testid="banner-checkbox"
                      />
                      <div className="flex-1">
                        <label
                          htmlFor="want_banner"
                          className="text-sm font-extrabold text-[#4a3209] cursor-pointer flex items-center gap-1.5"
                        >
                          <span aria-hidden="true">⭐</span>
                          {t('banner.postJobPromptTitle', 'Also put this job on the homepage banner')}
                        </label>
                        <p className="text-xs text-[#6b4a0e] font-medium mt-1">
                          {t('banner.postJobPromptBody', "From {{price}} credits — you'll pick the image and duration after posting.", { price: pricing.banner_price_3d || 35 })}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* SUBMIT */}
              <div className="space-y-3 pt-4">
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/dashboard')}
                    className="rounded-xl"
                    disabled={loading || autoSaving}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => saveDraft(false)}
                    className="rounded-xl"
                    disabled={loading || autoSaving || !formData.title?.trim()}
                  >
                    {autoSaving ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('postJob.saving', 'Saving...')}</>
                    ) : (
                      <><Save className="w-4 h-4 mr-2" />{t('postJob.saveDraft', 'Save Draft')}</>
                    )}
                  </Button>
                  <Button
                    type="submit"
                    className="rounded-xl flex-1"
                    disabled={loading}
                    data-testid="post-form-submit"
                  >
                    {loading ? 'Posting...' : `Post ${isGig ? 'Gig' : 'Job'}`}
                  </Button>
                </div>
                {autoSaving && (
                  <p className="text-xs text-muted-foreground text-center">{t('postJob.autoSaved', 'Auto-saving...')}</p>
                )}
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Post-creation homepage banner create flow (checkbox above opted in) */}
      {bannerModalJobId && (
        <BannerCreateModal
          jobId={bannerModalJobId}
          onClose={() => {
            setBannerModalJobId(null);
            if (pendingShareJob) {
              setShareJob(pendingShareJob);
              setPendingShareJob(null);
            } else {
              navigate('/dashboard');
            }
          }}
        />
      )}

      {shareJobDialog}

      <BottomNav profile={profile} />
    </div>
  );
};

export default PostJob;
